import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import Icon from '../ui/icon';
import Logo from '../ui/logo';
import { cn } from '../../lib/utils';
import { useTheme } from '../../contexts/ThemeContext';

interface AssistantHeaderProps {
  className?: string;
  onClearAllMessages?: () => void;
}

const AssistantHeader: React.FC<AssistantHeaderProps> = ({ className, onClearAllMessages }) => {
  const navigate = useNavigate();
  const { theme, toggleTheme } = useTheme();
  const [showClearDialog, setShowClearDialog] = useState(false);

  const handleBack = () => {
    navigate('/'); // Go to welcome page
  };

  const handleClearAll = () => {
    setShowClearDialog(true);
  };

  const confirmClearAll = () => {
    if (onClearAllMessages) {
      onClearAllMessages();
    }
    setShowClearDialog(false);
  };

  return (
    <header className={cn(
      "w-full border-b bg-background",
      className
    )}>
      <div className="max-w-4xl mx-auto px-4 flex h-16 items-center justify-between">
        {/* Left side - Back button */}
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="icon"
            onClick={handleBack}
            className="h-9 w-9"
          >
            <Icon name="arrow-left" size={18} />
          </Button>
        </div>

        {/* Center - Logo & Title */}
        <div className="flex items-center gap-2 flex-1 justify-start">
          <Logo size="md" variant="adaptive" />
          <div className="flex gap-1">
            <h1 className="text-md font-semibold">Ngọc • Trợ lý AI 24/7</h1>
          </div>
        </div>

        {/* Right side - Clear All & Theme Toggle */}
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="icon"
            onClick={handleClearAll}
            className="h-9 w-9"
            title="Xóa tất cả tin nhắn"
          >
            <Icon name="trash-2" size={18} />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={toggleTheme}
            className="h-9 w-9"
            title="Chuyển đổi chế độ sáng/tối"
          >
            <Icon name={theme === 'light' ? 'moon' : 'sun'} size={18} />
          </Button>
        </div>
      </div>

      {/* Clear All Messages Dialog */}
      <Dialog open={showClearDialog} onOpenChange={setShowClearDialog}>
        <DialogContent className="max-w-[95%] sm:max-w-md rounded-lg md:rounded-xl">
          <DialogHeader>
            <DialogTitle>Xóa tất cả tin nhắn</DialogTitle>
            <DialogDescription>
              Bạn có chắc chắn muốn xóa tất cả tin nhắn trong cuộc trò chuyện này không? 
              Hành động này không thể hoàn tác.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex gap-2 sm:gap-0">
            <Button
              variant="outline"
              onClick={() => setShowClearDialog(false)}
              className="flex-1 sm:flex-none"
            >
              Hủy
            </Button>
            <Button
              variant="destructive"
              onClick={confirmClearAll}
              className="flex-1 sm:flex-none"
            >
              Xóa tất cả
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </header>
  );
};

export default AssistantHeader;
